import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import Layout from "./components/Layout";
import AuthorizationRoutes from "./components/authorization/AuthorizationRoutes";
// @ts-expect-error: Unchecked type import
import authService from "./components/authorization/AuthorizeService";
// @ts-expect-error: Unchecked type import
import { WithACLAuthorizeRoute as AuthorizeRoute } from "./components/authorization/AuthorizeRoute";
import { lazy, useEffect } from "react";
import axios from "axios";
import { ChakraProvider, Spinner } from "@chakra-ui/react";
import theme from "@ui/theme";
import DocumentConfigurationProvider from "./context/DocumentConfigurationProvider";
import { AuthProvider } from "./context/AuthProvider";
import ConfigurationProvider from "./context/ConfigurationProvider";
import { useACL } from "@ui/components/document/DocumentACL";
import useIsMobile from "@ui/hooks/useIsMobile";
import { useEmployeeSelf } from "./services/useEmployee";
import SupplierPortal from "./pages/SupplierPortal";
import { UpdatePrompt } from "./components/UpdatePrompt";

const Directory = lazy(() => import("./pages/Directory"));
const GuestBooking = lazy(() => import("./pages/GuestBooking"));
const Supplier = lazy(() => import("./pages/Supplier"));
const SupplierBooking = lazy(() => import("./pages/SupplierBooking"));
const Holiday = lazy(() => import("./pages/Holiday"));
const RateCode = lazy(() => import("./pages/RateCode"));
const Masters = lazy(() => import("./pages/Masters"));
const Settings = lazy(() => import("./pages/Settings"));
const MealBooking = lazy(() => import("./pages/MealBooking"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount: number, error: any) => {
        if (error.reponse?.status === 500) return false;
        if (error.response?.status === 404) return false;
        return failureCount < 3;
      },
    },
  },
});

function DefaultRoute() {
  const { isPermitted } = useACL();
  const { data: employee } = useEmployeeSelf<any>();
  const isSuperAdmin = isPermitted(["group:super-admin"], undefined, undefined);
  const isGuestBookingAdmin = isPermitted(
    ["group:guest-booking-admin"],
    undefined,
    undefined,
  );
  const isMobile = useIsMobile();
  const isSupplier = employee?.category?.label?.toLowerCase() === "supplier";

  if (!employee) return <Spinner size="lg" />;

  if ((isSuperAdmin || isGuestBookingAdmin) && !isMobile) return <Dashboard />;
  else if (isSupplier)
    return <AuthorizeRoute element={<SupplierPortal />} desktopOnly={true} />;
  else return <MealBooking />;
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = () =>
  sentryCreateBrowserRouter(
    createRoutesFromElements(
      <>
        {AuthorizationRoutes.map((route) => {
          const { element, ...rest } = route;
          return <Route key={route.path} {...rest} element={element} />;
        })}
        <Route element={<Layout />}>
          <Route
            path="/"
            element={<AuthorizeRoute element={<DefaultRoute />} />}
          />
          <Route
            path="/MealBooking"
            element={
              <AuthorizeRoute
                element={<MealBooking />}
                excludeCategories={["supplier"]}
              />
            }
          />
          <Route
            path="/MealBooking/:id"
            element={
              <AuthorizeRoute
                element={<MealBooking />}
                excludeCategories={["supplier"]}
              />
            }
          />
          <Route
            path="/Dashboard"
            element={
              <AuthorizeRoute
                element={<Dashboard />}
                requiredRoles={[
                  "group:super-admin",
                  "group:guest-booking-admin",
                ]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/GuestBooking"
            element={
              <AuthorizeRoute
                element={<GuestBooking />}
                requiredRoles={[
                  "group:super-admin",
                  "group:guest-booking-admin",
                ]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/GuestBooking/:id"
            element={
              <AuthorizeRoute
                element={<GuestBooking />}
                requiredRoles={[
                  "group:super-admin",
                  "group:guest-booking-admin",
                ]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/Masters"
            element={
              <AuthorizeRoute
                element={<Masters />}
                requiredRoles={["group:super-admin"]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/Directory"
            element={
              <AuthorizeRoute
                element={<Directory />}
                requiredRoles={["group:super-admin", "group:employee-admin"]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/Holiday"
            element={
              <AuthorizeRoute
                element={<Holiday />}
                requiredRoles={["group:super-admin", "group:employee-admin"]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/RateCode"
            element={
              <AuthorizeRoute
                element={<RateCode />}
                requiredRoles={["group:super-admin"]}
                desktopOnly={true}
              />
            }
          />

          <Route
            path="/SupplierBooking"
            element={
              <AuthorizeRoute
                element={<SupplierBooking />}
                requiredRoles={[
                  "group:super-admin",
                  "group:guest-booking-admin",
                ]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/Supplier"
            element={
              <AuthorizeRoute
                element={<Supplier />}
                requiredRoles={["group:super-admin"]}
                desktopOnly={true}
              />
            }
          />
          <Route
            path="/Settings/:key?"
            element={
              <AuthorizeRoute
                element={<Settings />}
                requiredRoles={["group:super-admin"]}
                desktopOnly={true}
              />
            }
          />
          <Route path="*" element={<Navigate to={"/"} />} />
        </Route>
      </>,
    ),
  );

function App() {
  useEffect(() => {
    authService.getAccessToken().then((token: string) => {
      axios.defaults.headers.common["Authorization"] = "bearer " + token;
    });
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <ConfigurationProvider>
            <DocumentConfigurationProvider>
              <RouterProvider router={router()} />
            </DocumentConfigurationProvider>
          </ConfigurationProvider>
        </QueryClientProvider>
        <UpdatePrompt />
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App;
